import {ScreenTracker} from '../../../utils/ScreenTracker';
import {LocalStorage} from '../../../utils/LocalStorage';
import {DASHBO_WHATSAPP_URL} from '../../../settings/settings';
import {SupportChatService} from '../../support_chat/SupportChatService';

/**
 * Lo que dicen y hacen las pantallas cuando el cupo de cuentas del plan está lleno (UD-15). La
 * presentación es de cada pantalla (el modal del Panel, el cartel de Conexiones › Cuentas, un toast);
 * el texto, las salidas y la medición son éstos, para que digan lo mismo y se midan igual.
 */

const NS = 'account_quota';
const RAW = {escapeValue: false};

// «Liberar una cuenta»: la vista de cuentas conectadas a clientes, que es la de entrada de la pantalla.
export const FREE_UP_PATH = '/agency/ad-accounts';

/**
 * El evento de uso (`UserScreenActivity`): `account_quota_<acción>_<pantalla>`.
 * - `shown`: el aviso del cupo lleno ANTES de elegir, con sus dos salidas (el modal, Conexiones › Cuentas).
 * - `click_free` / `click_request`: las salidas de ese aviso.
 * - `rejected`: el toast de un rechazo por el tope, que no tiene salidas (Inversión sin control, las cuentas de
 *   la línea). Va aparte para que no entre en el denominador del criterio de UD-15 (C-cc-230-wildfi-07).
 */
export function quotaEventName(action, surface) {
    return `account_quota_${action}_${surface}`;
}

export function recordQuotaEvent(action, surface) {
    ScreenTracker.recordEvent(quotaEventName(action, surface));
}

/** Un número del cupo con el separador de miles del producto: 1500 → «1.500» (QA-VIS-cc-230-01). */
export function formatQuotaNumber(value) {
    return Math.round(Number(value) || 0).toLocaleString('es-AR');
}

function quotaNumbers(quota) {
    return {limit: formatQuotaNumber(quota.limit), used: formatQuotaNumber(quota.used)};
}

/** «Tu plan incluye 70 cuentas vinculadas a clientes y ya usas 70.» */
export function quotaFullTitle(t, quota) {
    return t(`${NS}.full_title`, {...quotaNumbers(quota), interpolation: RAW});
}

/** El contador de Conexiones › Cuentas: «170 de 500 cuentas del plan». */
export function quotaCounterText(t, quota) {
    return t(`${NS}.counter`, {...quotaNumbers(quota), interpolation: RAW});
}

/**
 * El toast de un rechazo por el tope en una pantalla SIN aviso propio (Inversión sin control, las cuentas
 * de la línea; el modal sólo cuando el rechazo no trajo números): con los números del rechazo
 * (`quotaFromLinkError`) y, si no los trajo, uno que igual dice qué hacer, con el verbo del aviso
 * («libera»). Nunca el `error_message` del backend.
 *
 * Sin números NO se usa el cupo que la pantalla leyó antes: el rechazo prueba que estaba viejo (decía
 * que había lugar), y «ya usas 69» sobre un tope de 70 sería falso.
 */
export function planLimitText(t, quotaOfTheRejection) {
    if (!quotaOfTheRejection) return t(`${NS}.rejected_plain`);
    return `${quotaFullTitle(t, quotaOfTheRejection)} ${t(`${NS}.full_text`)}`;
}

/**
 * «Pedir más cuentas»: abre el chat de soporte con el pedido escrito, y lo mide.
 *
 * Si el chat no está (no cargó), cae a WhatsApp, como `FreeTrialEnded.handleRequestMoreDays`:
 * `showNewMessage` caería a Intercom, que está apagado, y el botón no haría nada mientras la métrica
 * sumaba un pedido. Por WhatsApp llega de un número desconocido, así que el texto lleva el número de
 * agencia (C-cc-230-wildfi-08).
 */
export function requestMoreAccounts(t, quota, surface) {
    recordQuotaEvent('click_request', surface);
    const numbers = quota ? quotaNumbers(quota) : null;
    if (SupportChatService.isEnabled()) {
        SupportChatService.openWithMessage(numbers
            ? t(`${NS}.request_message`, {...numbers, interpolation: RAW})
            : t(`${NS}.request_message_plain`));
        return;
    }
    const agencyId = LocalStorage.fetchAgencyId();
    const key = numbers ? `${NS}.request_message_whatsapp` : `${NS}.request_message_whatsapp_plain`;
    const message = agencyId
        ? t(key, {...(numbers || {}), agencyId, interpolation: RAW})
        : t(numbers ? `${NS}.request_message` : `${NS}.request_message_plain`, {...(numbers || {}), interpolation: RAW});
    window.open(`${DASHBO_WHATSAPP_URL}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
}
